import React, { useMemo, useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import Button from "views/Group/components/Button";
import SelectComponent from "views/Group/components/SelectComponent";
import moment from "moment-jalaali";
import { THESIS_ITEM_TYPE_ID } from "utils";

function FilterFormPayamLib({
  filteredData,
  setFilteredData,
  originalData,
  filterOptions,
  itemTypeId = THESIS_ITEM_TYPE_ID?.FA
}) {
  const [selectedDate, setSelectedDate] = useState([]);
  const methods = useForm();
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  const defenseDateOptions = useMemo(() => {
    const startYear = itemTypeId === THESIS_ITEM_TYPE_ID?.EN ? moment().year() : moment().jYear();
    const endYear = itemTypeId === THESIS_ITEM_TYPE_ID?.EN ? 2001 : 1379;
    const options = [{ value: "-1", label: "همه" }];

    for (let year = startYear; year >= endYear; year--) {
      options.push({
        value: year.toString(),
        label: year.toString(),
      });
    }

    return options;
  }, []);

  const initQueries = {
    defencDate: "-1",
    authorStatement: "-1",
    degree: "-1",
    major: "-1",
    consultor: "-1",
    supervisor: "-1",
  };

  const checkIfHasActiveFilters = (queries) => {
    const values = [
      queries.authorStatement,
      queries.degree,
      queries.major,
      queries.consultor,
      queries.supervisor,
      queries.defencDate,
    ];

    return values.some((value) => {
      if (!value || value === "-1") return false;
      const stringValue = typeof value === "object" ? value.value : value;
      return stringValue !== "-1";
    });
  };

  const onSubmit = (queries) => {
    // بررسی آیا فیلتری فعال است
    const hasFilters = checkIfHasActiveFilters(queries);
    setHasActiveFilters(hasFilters);

    // تبدیل مقادیر object به string
    const authorStatementValue =
      typeof queries.authorStatement === "object"
        ? queries.authorStatement.value
        : queries.authorStatement;
    const degreeValue =
      typeof queries.degree === "object"
        ? queries.degree.value
        : queries.degree;
    const majorValue =
      typeof queries.major === "object" ? queries.major.value : queries.major;
    const consultorValue =
      typeof queries.consultor === "object"
        ? queries.consultor.value
        : queries.consultor;
    const supervisorValue =
      typeof queries.supervisor === "object"
        ? queries.supervisor.value
        : queries.supervisor;
    const defencDateValue =
      typeof queries.defencDate === "object"
        ? queries.defencDate.value
        : queries.defencDate;

    const filtered = originalData.filter((item, i) => {
      // فیلتر نویسنده
      if (
        authorStatementValue !== "-1" &&
        authorStatementValue !== item.authorStatement
      ) {
        return false;
      }

      // فیلتر مقطع
      if (degreeValue !== "-1" && degreeValue !== item.degree) {
        return false;
      }

      // فیلتر رشته/گرایش
      if (majorValue !== "-1") {
        const itemMajor = `${item.branch}${item.major ? ` - ${item.major}` : ""
          }`;
        if (majorValue !== itemMajor) {
          return false;
        }
      }

      // فیلتر استاد مشاور
      if (
        consultorValue !== "-1" &&
        item.consultor &&
        !item.consultor?.includes(consultorValue)
      ) {
        return false;
      }

      // فیلتر استاد راهنما
      if (
        supervisorValue !== "-1" &&
        item.supervisor &&
        !item.supervisor?.includes(supervisorValue)
      ) {
        return false;
      }

      // فیلتر سال دفاع
      if (defencDateValue !== "-1" && item.defencDate) {
        const itemYear = item.defencDate.split("/")[0];
        if (defencDateValue !== itemYear) {
          return false;
        }
      }

      return true;
    });

    setFilteredData(filtered);
  };

  const resetForm = () => {
    methods.reset(initQueries);
    setSelectedDate([]);
    setFilteredData(originalData);
    setHasActiveFilters(false);
  };

  useEffect(() => {
    methods.reset(initQueries);
  }, [originalData]);

  return (
    <FormProvider {...methods}>
      <div className="tw-mb-5">
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-x-[30px] md:tw-gap-x-10 tw-gap-y-[26px]">
            <div>
              <SelectComponent
                name="authorStatement"
                label="دانشجو"
                placeholder="همه"
                options={[
                  { value: "-1", label: "همه" },
                  ...(filterOptions?.authors || []),
                ]}
              />
            </div>
            <div>
              <SelectComponent
                name="degree"
                label="مقطع"
                placeholder="همه"
                options={[
                  { value: "-1", label: "همه" },
                  ...(filterOptions?.degrees || []),
                ]}
              />
            </div>
            <div>
              <SelectComponent
                name="major"
                label="رشته / گرایش"
                placeholder="همه"
                options={[
                  { value: "-1", label: "همه" },
                  ...(filterOptions?.majors || []),
                ]}
              />
            </div>
            <div>
              <SelectComponent
                name="consultor"
                label="استاد مشاور"
                placeholder="همه"
                options={[
                  { value: "-1", label: "همه" },
                  ...(filterOptions?.consultors || []),
                ]}
              />
            </div>
            <div>
              <SelectComponent
                name="supervisor"
                label="استاد راهنما"
                placeholder="همه"
                options={[
                  { value: "-1", label: "همه" },
                  ...(filterOptions?.supervisors || []),
                ]}
              />
            </div>
            <div>
              <SelectComponent
                name="defencDate"
                label="سال دفاع"
                placeholder="همه"
                options={defenseDateOptions}
              />
            </div>
            <div className="md:tw-pt-[37px] tw-col-span-2 sm:tw-col-span-1">
              <Button
                type="button"
                onClick={resetForm}
                layout="secondary"
                disabled={!hasActiveFilters}
              >
                {hasActiveFilters ? "حذف فیلترها" : "فیلتری فعال نیست"}
              </Button>
            </div>
            <div className="md:tw-pt-[37px] tw-col-span-2 sm:tw-col-span-1">
              <Button type="submit" layout="primary">
                {hasActiveFilters ? "اعمال فیلترها" : "نمایش همه"}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </FormProvider>
  );
}

export default FilterFormPayamLib;
